// frontend/src/features/livestock/RecentlySorted.tsx
// The inbox grid's list of what was sorted this session (plan 4.4): the last
// 20 assigns and discards with the photo, the organism or "Discarded", and
// Undo within 15 minutes; one being sent, or one that failed with Retry and
// Open. Closed under its heading until something fails or the inbox is empty.

import { useEffect, useState } from 'react';
import { Box, Button, Group, Paper, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { useIsMobile } from '@/hooks/useIsMobile';
import type { Livestock } from '@/types/livestock';
import { formatPhotoDay } from './photoInboxFormat';
import { BRAND_TEXT } from './brandText';
import {
  canUndo,
  TANK_SHOT_NAME,
  targetOf,
  useSortRecords,
  wasSortedBefore,
  type SortRecord,
} from './photoInboxSorting';
import { useInboxSorting, type InboxSorting } from './useInboxSorting';
import classes from './RecentlySorted.module.css';

export interface RecentlySortedProps {
  tankId: string;
  /** The tank's livestock, to name an organism a photo turned out to be on. */
  organisms?: Livestock[];
  /** Start open (the inbox is empty). */
  defaultOpen?: boolean;
}

function title(record: SortRecord): string {
  if (record.kind === 'discard') return 'Discarded';
  if (targetOf(record) === 'tank') return TANK_SHOT_NAME;
  return record.resultLivestockName ?? record.livestockName ?? 'An organism';
}

function detail(record: SortRecord): string {
  const photo = `Photo from ${formatPhotoDay(record.takenAt)}`;
  switch (record.status) {
    case 'pending':
      return `${photo} · ${record.kind === 'assign' ? 'Assigning…' : 'Discarding…'}`;
    case 'failed':
      return `Couldn't ${record.kind}: ${record.message ?? 'something went wrong.'}`;
    case 'gone':
      return `${photo} · ${record.message ?? 'sorted already'}`;
    case 'undoing':
      return `${photo} · Undoing…`;
    case 'undone':
      return `${photo} · Undone`;
    default:
      if (record.undoMessage) return record.undoMessage;
      if (!wasSortedBefore(record)) return photo;
      return targetOf(record) === 'tank'
        ? `${photo} · it was a tank shot already`
        : `${photo} · it was on this organism already`;
  }
}

function Row({
  record,
  sorting,
  isMobile,
}: {
  record: SortRecord;
  sorting: InboxSorting;
  isMobile: boolean;
}): JSX.Element {
  const buttonHeight = isMobile ? 44 : 32;
  const failed = record.status === 'failed';
  return (
    <Group component="li" gap="sm" wrap="nowrap" className={classes.row}>
      {record.thumbUrl ? (
        <img src={record.thumbUrl} alt="" className={classes.thumb} />
      ) : (
        <Box className={classes.thumb} />
      )}
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text size="sm" fw={500} truncate>
          {title(record)}
        </Text>
        <Text size="xs" c={failed || record.undoMessage ? 'red.8' : 'gray.7'} lineClamp={2}>
          {detail(record)}
        </Text>
      </Box>
      <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
        {failed && record.canRetry && (
          <Button
            size="compact-sm"
            h={buttonHeight}
            variant="light"
            color="red"
            onClick={() => sorting.retry(record.key)}
          >
            Retry
          </Button>
        )}
        {failed && (
          <Button
            size="compact-sm"
            h={buttonHeight}
            variant="default"
            onClick={() => sorting.open(record.key)}
          >
            Open
          </Button>
        )}
        {canUndo(record) && (
          <Button
            size="compact-sm"
            h={buttonHeight}
            variant="subtle"
            onClick={() => sorting.undo(record.key)}
            style={BRAND_TEXT}
          >
            Undo
          </Button>
        )}
      </Group>
    </Group>
  );
}

export function RecentlySorted({
  tankId,
  organisms,
  defaultOpen = false,
}: RecentlySortedProps): JSX.Element | null {
  const isMobile = useIsMobile();
  const records = useSortRecords(tankId);
  const sorting = useInboxSorting(tankId, organisms);
  const failed = records.some((record) => record.status === 'failed' && !record.dismissed);
  const [open, setOpen] = useState(defaultOpen || failed);
  // Opens for a failure, or once the inbox is empty; stays open after a Retry.
  useEffect(() => {
    if (defaultOpen || failed) setOpen(true);
  }, [defaultOpen, failed]);

  if (records.length === 0) return null;
  return (
    <Paper withBorder radius={12} className={classes.root}>
      <UnstyledButton
        className={classes.header}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Group justify="space-between" wrap="nowrap">
          <Text fw={600} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Recently sorted
          </Text>
          <Group gap={6} wrap="nowrap">
            <Text size="sm" c="gray.7" className={classes.count}>
              {records.length}
            </Text>
            <IconChevronDown size={16} className={classes.chevron} data-open={open || undefined} />
          </Group>
        </Group>
      </UnstyledButton>
      {open && (
        <ul className={classes.list} aria-label="Recently sorted">
          {records.map((record) => (
            <Row key={record.key} record={record} sorting={sorting} isMobile={isMobile} />
          ))}
        </ul>
      )}
    </Paper>
  );
}
